import React, { useState } from 'react';
import { detailWrap } from 'styles/table';

import Action from './Action';
import useDetail from '../useDetail';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { useNavigate } from 'react-router-dom';
import { EVENT_MENU_URL } from 'data/menu';
import { detailPtagStyles } from './styles';
import Editor from './Editor';
import useEditor from './useEditor';
import useOpenKakao from './useOpenKakao';
import OpenKakao from './OpenKakao';
import { getEventStatus } from '../utils';
import Partner from './Partner';
import useModal from 'hooks/useModal';
import ManageContents from './ManageContents';
import PreviewModal from './PreviewModal';
import useParticipantCount from '../useParticipantCount';

dayjs.locale('ko');
const FORMAT = 'MM월 DD일 (ddd)';

function Main() {
  const navigate = useNavigate();
  const { event } = useDetail();
  const {
    title,
    value,
    isOpenEditor,
    handleOpen,
    handleClose,
    handleSaveEditor,
  } = useEditor({ event });
  const { handleModal } = useModal();
  const { isOpenKakao, setIsOpenKakao } = useOpenKakao();
  const [isOpenPartner, setIsOpenPartner] = useState(false);
  const [isOpenPreview, setIsOpenPreview] = useState(false);

  const handleQuestion = (type: 'additional' | 'feedback') => {
    navigate(`${EVENT_MENU_URL.행사}/${event?.id}/${type}`);
  };

  const participantCount = useParticipantCount(event);

  if (!event) return null;

  return (
    <>
      <div
        css={(theme) => [
          detailWrap(theme),
          { marginTop: '24px', display: 'flex', position: 'relative' },
        ]}
      >
        <Action event={event} onPreview={() => setIsOpenPreview(true)} />
        {event.mainImageUrl && (
          <div
            css={{
              width: '200px',
              marginRight: '16px',
            }}
          >
            <img
              src={event.mainImageUrl}
              alt="thumnail"
              css={{ borderRadius: 8, width: '100%' }}
            />
          </div>
        )}

        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '8px 0',
          }}
        >
          <div>
            <p css={{ fontWeight: 'bold', fontSize: '16px' }}>{event.title}</p>
            <ul
              css={{
                marginTop: '20px',
                display: 'flex',
                flexDirection: 'row',
                gap: '0 32px',
                '> div > li': {
                  display: 'flex',
                  fontSize: '13px',
                  marginBottom: '12px',
                  alignItems: 'center',
                  'p:nth-of-type(1)': {
                    fontWeight: 'bold',
                    color: '#8d8d8d',
                    textAlign: 'justify',
                    width: '100px',
                  },
                },
              }}
            >
              <div>
                <li>
                  <p>단체</p>
                  <p>{event.organizationName}</p>
                </li>
                <li>
                  <p>상 태</p>
                  <p>
                    {getEventStatus(
                      event.eventStatus,
                      event.startDate,
                      event.endDate,
                      event.enrollStartDate,
                      event.enrollEndDate,
                    )}
                  </p>
                </li>
                <li>
                  <p>모집기간</p>
                  <p>{`${dayjs(event.enrollStartDate).format(FORMAT)} ~ ${dayjs(event.enrollEndDate).format(FORMAT)}`}</p>
                </li>
                <li>
                  <p>일 시</p>
                  <p>{`${dayjs(event.startDate).format(FORMAT)} ~ ${dayjs(event.endDate).format(FORMAT)}`}</p>
                </li>
                <li>
                  <p>장 소</p>
                  <p>{event.locationName}</p>
                </li>
                <li>
                  <p>모집인원</p>
                  <p>{participantCount}</p>
                </li>
                <li>
                  <p>인증기관</p>
                  <p>{event.certificationAgency.join(', ')}</p>
                </li>
                <li>
                  <p>봉사시간</p>
                  <p>{Number(event.volunteerHours)}</p>
                </li>
              </div>
              <div>
                <li>
                  <p>추가정보</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => handleQuestion('additional')}
                  >
                    {event.additionalId ? '수정' : '등록'}
                  </p>
                </li>
                <li>
                  <p>피드백 가이드</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => handleOpen('feedbackGuide')}
                  >
                    {event.feedbackGuide ? '수정' : '등록'}
                  </p>
                </li>

                <li>
                  <p>피드백</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => handleQuestion('feedback')}
                  >
                    {event.feedbackId ? '수정' : '등록'}
                  </p>
                </li>
                <li>
                  <p>행사 상세</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => handleOpen('description')}
                  >
                    {event.description ? '수정' : '등록'}
                  </p>
                </li>
                <li>
                  <p>행사 공지사항</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => handleOpen('notice')}
                  >
                    {event.notice ? '수정' : '등록'}
                  </p>
                </li>
                <li>
                  <p>행사 후원사</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => setIsOpenPartner(true)}
                  >
                    관리
                  </p>
                </li>
                <li>
                  <p>오픈채팅방 링크</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() => setIsOpenKakao(true)}
                  >
                    {event.openTalkId ? '수정' : '등록'}
                  </p>
                </li>
                <li>
                  <p>콘텐츠</p>
                  <p
                    css={detailPtagStyles}
                    onClick={() =>
                      handleModal('', <ManageContents event={event} />, <></>)
                    }
                  >
                    관리
                  </p>
                </li>
              </div>
            </ul>
          </div>
        </div>
      </div>
      {isOpenEditor && (
        <Editor
          title={title}
          value={value}
          onSave={handleSaveEditor}
          onClose={handleClose}
        />
      )}
      {isOpenKakao && (
        <OpenKakao event={event} onClose={() => setIsOpenKakao(false)} />
      )}
      {isOpenPartner && (
        <Partner event={event} onClose={() => setIsOpenPartner(false)} />
      )}
      {isOpenPreview && (
        <PreviewModal event={event} onClose={() => setIsOpenPreview(false)} />
      )}
    </>
  );
}

export default Main;
